import React, { useMemo } from "react";
import { AlertColor, Paper, PaperProps, styled, Box } from "@mui/material";
import AlertTitle, { AlertIcon, iconMapping } from "./AlertTitle";
import AlertActionButton from "./AlertActionButton";
import { ApiErrorResponse } from "@common/api";
import { SxProps, useTheme } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getCurrentSizeProfile,
  responsiveFontSize,
} from "@common/components/Typography/style";
import { Priority } from "@services/common/types";

export type TicketColor = Priority;
type AlertVariant = "filled" | "outlined";

export type AlertProps = {
  severity: AlertColor;
  title?: string;
  time?: string;
  icon?: any;
  alarmId?: number;
  clearFunction?: (id: number) => void;
  notif?: boolean;
  showDetails?: (value: boolean) => void;
  details?: ApiErrorResponse;
  priority?: TicketColor;
  redirectTo?: string;
  markdownMessage?: string;
  variant?: AlertVariant;
  sx?: SxProps;
};

type AlertContainerProps = Omit<PaperProps, "variant"> &
  Pick<AlertProps, "severity" | "priority" | "variant"> & {
    ref?: React.Ref<HTMLDivElement>;
  };

const AlertContainer = ({
  ref,
  severity,
  priority,
  variant,
  sx,
  ...props
}: AlertContainerProps) => {
  const theme = useTheme();
  const buildOutlined = () => ({
    backgroundColor: "transparent",
    backgroundImage: "none",
    border: `1px solid ${theme.palette[severity ?? "success"].light}`,
    borderRadius: theme.radius.small,
    padding: `0 ${theme.spacing(2)}`,
    boxShadow: "none",

    "& > .MuiBox-root": {
      padding: `${theme.spacing(1)} 0`,
      color: theme.palette.common.grey.gBFBFBF,
      fontSize: getCurrentSizeProfile().small,
      ...responsiveFontSize(theme, "small"),
      fontWeight: "400",
      display: "flex",
      alignItems: "center",
      "& .MuiSvgIcon-root": {
        color: theme.palette[severity ?? "success"].light,
        width: "1.4em",
        height: "1.4em",
      },
    },
  });
  const getBackgroundColor = () => {
    if (priority === undefined) return theme.palette[severity].main;
    else
      return theme.palette.ticket[priority][
        theme.palette.mode === "dark" ? "dark" : "light"
      ];
  };
  const getFontColor = () => {
    if (priority === undefined) return theme.palette.text.primary;
    else
      return theme.palette.ticket[priority][
        theme.palette.mode === "dark" ? "light" : "dark"
      ];
  };
  const buildFilled = () => ({
    padding: "0.4rem",
    borderRadius: "0.3rem",
    overflow: "hidden",
    wordWrap: "break-word",
    width: "28rem",
    color: getFontColor(),
    backgroundColor: getBackgroundColor(),
  });

  const getComputeStyle = () => {
    switch (variant) {
      case "outlined":
        return buildOutlined();
      default:
        return buildFilled();
    }
  };

  const computeStyle = useMemo(() => getComputeStyle(), [variant, theme]);

  return (
    <Paper
      ref={ref}
      {...props}
      sx={[computeStyle, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
};
const AlertContent = styled(Box)({
  padding: "0rem 2.5rem",
  lineHeight: 1,
  color: "white",
  a: {
    color: "white",
  },
  whiteSpace: "pre-line",
});

type AlertPropsWithRef = React.PropsWithChildren<AlertProps> & {
  ref?: React.Ref<HTMLDivElement>;
};

const Alert = ({
  ref,
  severity = "error",
  title,
  icon,
  time,
  children,
  alarmId,
  clearFunction,
  notif,
  showDetails,
  details,
  priority,
  redirectTo,
  markdownMessage,
  variant = "filled",
  sx,
}: AlertPropsWithRef) => (
  <AlertContainer
    ref={ref}
    severity={severity}
    priority={priority}
    variant={variant}
    sx={sx}
  >
    {title && (
      <AlertTitle
        severity={severity}
        icon={icon}
        iconSx={
          priority
            ? (theme) => ({ color: theme.palette.ticket[priority].main })
            : undefined
        }
        time={time}
        alarmId={alarmId}
        clearFunction={clearFunction}
        notif={notif}
        redirectTo={redirectTo}
      >
        {title}
      </AlertTitle>
    )}

    <AlertContent
      sx={{
        whiteSpace: markdownMessage ? "normal" : "pre-line",
        ...(priority
          ? (theme) => ({
              color:
                theme.palette.ticket[priority][
                  theme.palette.mode === "dark" ? "light" : "dark"
                ],
            })
          : {}),
      }}
      data-testid="alert-content"
    >
      {markdownMessage ? (
        <ReactMarkdown
          children={markdownMessage ?? ""}
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ node, href, children, ...props }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            ),
          }}
        />
      ) : (
        <>
          {variant === "outlined" && title === undefined && (
            <AlertIcon
              sx={
                priority !== undefined
                  ? (theme) => ({
                      color: theme.palette.ticket[priority].main,
                    })
                  : {}
              }
            >
              {icon || iconMapping[severity]}
            </AlertIcon>
          )}
          {children}
        </>
      )}
    </AlertContent>

    {details && showDetails !== undefined && (
      <AlertActionButton showDetails={showDetails} error={details.data.error} />
    )}
  </AlertContainer>
);

export default Alert;
